import React, { useMemo, useRef } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { eggState } from '../../lib/eggs';

// Four times of day. Each sets how dark it is (`night`, read by stars, moon, picture lights,
// wildlife…) and how golden the light is (`dusk`, sunrise/sunset glow). `dawn` picks the softer,
// pinker morning palette instead of the deep orange of evening.
export const TIMES = {
  morning:   { label: 'Morning',   icon: '🌅', night: 0.04, dusk: 0.72, dawn: 1 },
  afternoon: { label: 'Afternoon', icon: '☀️', night: 0,    dusk: 0,   dawn: 0 },
  evening:   { label: 'Evening',   icon: '🌇', night: 0.42, dusk: 1,   dawn: 0 },
  night:     { label: 'Night',     icon: '🌙', night: 1,    dusk: 0,   dawn: 0 },
};
export const TIME_IDS = Object.keys(TIMES);

// From the visitor's own clock: night 10 pm – 4 am, morning until noon, afternoon until 5 pm,
// evening until 10 pm. Decided afresh on every visit; the visitor can change it any time.
export const timeOfDayFor = (date = new Date()) => {
  const hour = date.getHours();
  if (hour >= 22 || hour < 4) return 'night';
  if (hour < 12) return 'morning';
  if (hour < 17) return 'afternoon';
  return 'evening';
};
export const createSkyState = (timeOfDay) => ({ dusk: TIMES[timeOfDay].dusk, dawn: TIMES[timeOfDay].dawn });

// `nightRef.current` (0 day … 1 night) and `skyRef.current` ({ dusk, dawn }) glide to the chosen
// time of day. Scene parts read them every frame, so changes are a fade, with no React re-renders.
const BLEND_SPEED = 0.8; // full change in about 1.25 s
const glide = (current, target, step) => (Math.abs(target - current) <= step ? target : current + Math.sign(target - current) * step);

export const DayNightDriver = ({ timeOfDay, isNight, nightRef, skyRef }) => {
  const { scene } = useThree();
  useFrame((_, delta) => {
    const t = TIMES[timeOfDay] || (isNight ? TIMES.night : TIMES.afternoon);
    const step = delta * BLEND_SPEED;
    nightRef.current = glide(nightRef.current, t.night, step);
    if (skyRef) {
      skyRef.current.dusk = glide(skyRef.current.dusk, t.dusk, step);
      skyRef.current.dawn = glide(skyRef.current.dawn, t.dawn, step);
    }
    // Dim the HDR reflections (glass roof, metals) at night.
    // (and soften them at sunrise / sunset, so the low golden sun shapes the light)
    scene.environmentIntensity = THREE.MathUtils.lerp(1, 0.12, nightRef.current) * (1 - 0.45 * (skyRef ? skyRef.current.dusk : 0));
  });
  return null;
};

// Golden-hour colours: soft peach at sunrise, deep orange at sunset.
export const DUSK_MORNING = new THREE.Color('#ffd2a1');
export const DUSK_EVENING = new THREE.Color('#ff8c42');
export const duskColor = (sky, out) => out.copy(DUSK_EVENING).lerp(DUSK_MORNING, sky ? sky.dawn : 0);

// Warm "picture light" glow over each painting at night.
// IMPORTANT: these are additive glow planes, NOT real lights. Turning real lights on/off changes the
// scene's light count, which forces three.js to recompile every shader in the scene. On iPhones that
// spike crashed the tab (Safari then reloads the page).
const makePictureGlowTexture = () => {
  const w = 256, h = 256;
  const c = document.createElement('canvas');
  c.width = w; c.height = h;
  const ctx = c.getContext('2d');
  // Elliptical pool of light, brightest near the top where the lamp would be.
  ctx.translate(w / 2, h * 0.32);
  ctx.scale(1, 1.35);
  const g = ctx.createRadialGradient(0, 0, 0, 0, 0, w * 0.55);
  g.addColorStop(0, 'rgba(255, 214, 150, 1)');
  g.addColorStop(0.45, 'rgba(255, 190, 120, 0.55)');
  g.addColorStop(1, 'rgba(255, 170, 100, 0)');
  ctx.fillStyle = g;
  ctx.fillRect(-w, -h, w * 2, h * 2);
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
};

const PictureGlow = ({ painting, wallHeight, nightRef, texture }) => {
  const matRef = useRef();
  const { position, rotation, size } = useMemo(() => {
    const euler = new THREE.Euler(painting.rotation.x, painting.rotation.y, painting.rotation.z);
    const forward = new THREE.Vector3(0, 0, 1).applyEuler(euler);
    const pos = new THREE.Vector3(painting.position.x, wallHeight / 2 + 0.35, painting.position.z)
      .add(forward.multiplyScalar(0.12)); // just in front of the canvas and frame
    return {
      position: pos.toArray(),
      rotation: [euler.x, euler.y, euler.z],
      size: [painting.size.width + 2.2, painting.size.height + 2.6],
    };
  }, [painting, wallHeight]);

  useFrame(() => {
    if (!matRef.current) return;
    // after the midnight chime (WallClock.jsx) the picture lights pulse twelve times with the bells
    const since = (performance.now() - eggState.chimeAt) / 1000;
    const pulse = eggState.chimeAt > 0 && since < 19.5 ? Math.pow(Math.max(0, Math.cos((since / 1.6) * Math.PI * 2)), 6) * 0.45 : 0;
    matRef.current.opacity = nightRef.current * 0.32 + pulse;
  });

  return (
    <mesh position={position} rotation={rotation} renderOrder={2}>
      <planeGeometry args={size} />
      <meshBasicMaterial
        ref={matRef}
        map={texture}
        transparent
        opacity={0}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        toneMapped={false}
      />
    </mesh>
  );
};

export const PictureLights = ({ paintings, wallHeight, nightRef }) => {
  const texture = useMemo(makePictureGlowTexture, []);
  return (
    <>
      {paintings.map((p) => (
        <PictureGlow key={p.id} painting={p} wallHeight={wallHeight} nightRef={nightRef} texture={texture} />
      ))}
    </>
  );
};

// Compiles every shader in the scene once, right after it first renders, so switching
// night/weather later never has to compile anything (a big memory spike on phones).
export const ShaderPrewarm = () => {
  const { gl, scene, camera } = useThree();
  const frame = useRef(0);
  useFrame(() => {
    frame.current += 1;
    // Twice: on the first frame, and again once late-loading parts (textures, models) are in.
    if (frame.current !== 1 && frame.current !== 20) return;
    // compileAsync lets the GPU driver build the programs in the background (where the browser
    // supports it), so the first frame doesn't freeze while dozens of shaders link at once.
    try {
      if (gl.compileAsync) gl.compileAsync(scene, camera).catch(() => {});
      else gl.compile(scene, camera);
    } catch (e) { /* prewarming is best-effort */ }
  });
  return null;
};

// Calls onReady once everything inside the same <Suspense> has loaded (it only mounts then).
export const ReadySignal = ({ onReady }) => {
  const done = useRef(false);
  useFrame(() => {
    if (done.current) return;
    done.current = true;
    onReady?.();
  });
  return null;
};

// Initial mode, from the visitor's own clock (their device's local time): night from 10 pm to 4 am.
// It is decided afresh on every visit; the ☀️/🌙 button switches it for the rest of the visit.
export const getInitialTimeOfDay = () => timeOfDayFor();

